import React, { useState } from 'react';
import styles from '../../styles/components/sections/TestimonialsSection.module.css';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';
import { isSafari } from '../../utils/helpers/utils';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { OptimizedImage } from '../common';

/**
 * Testimonials Section Component
 * Displays customer testimonials with carousel functionality
 *
 * @param {Object} props
 * @param {Object} props.data - The testimonials data
 * @param {String} props.data.title - Section title
 * @param {String} props.data.description - Section description
 * @param {Array} props.data.testimonials - Array of testimonial objects
 * @param {String} props.className - Optional additional CSS class
 */

const TestimonialsSection = ({ data, className = '' }) => {
    const [currentItem, setCurrentItem] = useState(0);
    const { isDarkMode, isThemeInitialized } = useDarkMode();

    if (!data) return null;

    const testimonials = data.testimonials || [];

    // Wait for theme initialization to prevent flash
        // Remove theme initialization check to prevent hydration errors
    // Component will render with default theme state and update after hydration

    const onClickPrevItem = () => {
        setCurrentItem(prev => (prev === 0 ? testimonials.length - 1 : prev - 1));
    };

    const onClickNextItem = () => {
        setCurrentItem(prev => (prev === testimonials.length - 1 ? 0 : prev + 1));
    };

    const getPreviousItem = () => {
        return currentItem === 0 ? testimonials.length - 1 : currentItem - 1;
    };

    const getNextItem = () => {
        return currentItem === testimonials.length - 1 ? 0 : currentItem + 1;
    };

    const previousItem = getPreviousItem();
    const nextItem = getNextItem();

    return (
        <section 
            className={`tw-container py-6 ${isDarkMode ? 'bg-dark-mode' : 'bg-dark-orange'} ${className}`}
            style={{ 
                backgroundColor: isDarkMode ? 'var(--bg-dark)' : '#16110d',
                transition: 'background-color 0.3s ease'
            }}
        >
            <div className={`row flex-md-row-reverse align-items-center ${styles.row}`}>
                <div className="col-xl-5 col-12 pe-sm-5 ps-xl-0 ps-sm-5 pt-xl-0 pt-5">
                    <h2 className={`text-md-center ${styles['section-heading']}`}>{data?.title}</h2>
                    <p className={styles['section-description']}>{data?.description}</p>
                </div>
                <div className="col-xl-1 col-sm-2 d-sm-flex d-none">
                    <div className="d-flex flex-column justify-content-center h-100">
                        <div className={styles.controls}>
                            <button onClick={onClickPrevItem} className={`${styles['round-button']} my-3`}>
                                <OptimizedImage src="/assets/arrow-up.svg" alt="arrow-up" height={25} width={13} priority={false} loading="lazy" quality={65} sizes="15px" />
                            </button>
                            <button onClick={onClickNextItem} className={`${styles['round-button']} my-3`}>
                                <OptimizedImage src="/assets/arrow-down.svg" alt="arrow-down" height={25} width={13} priority={false} loading="lazy" quality={65} sizes="15px" />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="col-xl-6 col-sm-10">
                    <div className={styles.carousel}>
                        <div className={styles['item-wrapper']}>
                            {/* Previous item content here */}
                            <div className={`${styles.item} ${styles.previous} d-sm-block d-none`}>
                                <div className="d-flex flex-column gap-3 px-sm-5 py-2">
                                    <div className="d-flex justify-content-between">
                                        <p className={styles['ct-heading']}>
                                            {testimonials[nextItem]?.title}
                                        </p>
                                    </div>
                                    <p className={styles['ct-review']}>{testimonials[nextItem]?.review}</p>
                                    <div className="d-flex gap-3">
                                        <OptimizedImage
                                            src={replaceImageUrl(testimonials[nextItem]?.clientImage?.url)}
                                            alt="Previous Item"
                                            className={styles['author-img']}
                                            priority={false}
                                            loading="lazy"
                                            width={50}
                                            height={50}
                                            quality={65}
                                            sizes="50px"
                                        />
                                        <div className="d-flex flex-column">
                                            <p className={styles['ct-heading']}>{testimonials[nextItem]?.clientName}</p>
                                            <p className={styles['ct-review']}>{testimonials[nextItem]?.clientSubTitle}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Current item content here */}
                            <div className={`${styles.item} ${styles.current}`}>
                                <div className="d-flex flex-column gap-3 px-sm-5 py-2">
                                    <div className="d-flex justify-content-between">
                                        <p className={styles['ct-heading']}>
                                            {testimonials[currentItem]?.title}
                                        </p>
                                    </div>
                                    <p className={styles['ct-review']}>{testimonials[currentItem]?.review}</p>
                                    <div className="d-flex gap-3">
                                        <OptimizedImage
                                            src={replaceImageUrl(testimonials[currentItem]?.clientImage?.url)}
                                            alt="Current Item"
                                            priority={false}
                                            loading="lazy"
                                            className={styles['author-img']}
                                            width={50}
                                            height={50}
                                            quality={65}
                                            sizes="50px"
                                        />
                                        <div className="d-flex flex-column">
                                            <p className={styles['ct-heading']}>{testimonials[currentItem]?.clientName}</p>
                                            <p className={styles['ct-review']}>{testimonials[currentItem]?.clientSubTitle}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Next item content here */}
                            <div className={`${styles.item} ${styles.next} d-sm-block d-none`}>
                                <div className="d-flex flex-column gap-3 px-sm-5 py-2">
                                    <div className="d-flex justify-content-between">
                                        <p className={styles['ct-heading']}>
                                            {testimonials[previousItem]?.title}
                                        </p>
                                    </div>
                                    <p className={styles['ct-review']}>{testimonials[previousItem]?.review}</p>
                                    <div className="d-flex gap-3">
                                        <OptimizedImage
                                            src={replaceImageUrl(testimonials[previousItem]?.clientImage?.url)}
                                            alt="Next Item"
                                            className={styles['author-img']}
                                            priority={false}
                                            loading="lazy"
                                            width={50}
                                            height={50}
                                            quality={65}
                                            sizes="50px"
                                        />
                                        <div className="d-flex flex-column">
                                            <p className={styles['ct-heading']}>{testimonials[previousItem]?.clientName}</p>
                                            <p className={styles['ct-review']}>{testimonials[previousItem]?.clientSubTitle}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex d-sm-none">
                    <div className="d-flex justify-content-center w-100 align-items-center">
                        <div className={`${styles.controls} d-flex gap-2`}>
                            <button onClick={onClickPrevItem} className={`${styles['round-button']} my-3`}>
                                <OptimizedImage src="/assets/arrow-left.svg" alt="arrow-left" height={25} width={13} priority={false} loading="lazy" quality={65} sizes="15px" />
                            </button>
                            <button onClick={onClickNextItem} className={`${styles['round-button']} my-3`}>
                                <OptimizedImage src="/assets/arrow-right.svg" alt="arrow-right" height={25} width={13} priority={false} loading="lazy" quality={65} sizes="15px" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;
